/* eslint-disable @typescript-eslint/no-empty-object-type */
import type {
  ApolloCache,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client';
import { CombinedGraphQLErrors, isErrorLike } from '@apollo/client/errors';
import { useMutation as useApolloMutation } from '@apollo/client/react';
import type {
  FieldNode,
  OperationDefinitionNode,
  FormattedExecutionResult,
} from 'graphql';
import { useCallback, useMemo } from 'react';

import { useMangopayConsentContext } from '#src/contexts/mangopayConsent/index.ts';
import { useRestrictedAccessContext } from '#src/contexts/restrictedAccess/index.ts';
import { Level } from '#src/contexts/snackNotification/index.ts';
import { MutationError } from '#src/errors/mutation.ts';
import { NetworkError } from '#src/errors/network.ts';
import { notificationsQueue } from '#src/hooks/useNotificationsQueue.ts';
import { sendSafeError } from '#src/lib/error.ts';

type Prettify<T> = { [K in keyof T]: T[K] } & {};

export interface Error {
  message: string;
  code?: number;
}

type MutationPayload = {
  errors?: Error[];
};

type Options<T, V extends OperationVariables, CV extends Partial<V> = {}> = {
  showErrorsWithSnackNotification?: boolean;
  showErrorsInForm?: boolean;
  sendErrors?: boolean;
  warnIfNoErrorHandling?: boolean;
} & Omit<useApolloMutation.Options<T, V>, 'variables'> & {
    variables?: {
      [K in keyof CV]: K extends keyof V ? CV[K] : never;
    };
  };

export const errorCodes = {
  unverifiedEmail: 20000,
  unverifiedPhoneNumber: 20010,
  consentRequired: 24001,
  consentAlreadyGiven: 24002,
  cardPackSoldOut: 27000,
};

// Handle type safe mutation variables
// https://github.com/apollographql/apollo-client/pull/12572
type MakeRequiredVariablesOptional<
  TVariables extends OperationVariables,
  TConfiguredVariables extends Partial<TVariables>,
> = Prettify<
  {
    [K in keyof TVariables as K extends keyof TConfiguredVariables
      ? K
      : never]?: TVariables[K];
  } & Omit<TVariables, keyof TConfiguredVariables>
>;

type Result<TData> = Omit<
  FormattedExecutionResult<TData>,
  'errors' | 'data'
> & {
  data: useApolloMutation.Result<TData>['data'];
  errors: Array<{ message: string; code?: number }>;
  success: boolean;
};

type MutationFunction<
  TData,
  TVariables extends OperationVariables,
  TCache extends ApolloCache = ApolloCache,
> = (
  ...[options]: {} extends TVariables
    ? [
        options?: useApolloMutation.MutationFunctionOptions<
          TData,
          TVariables,
          TCache
        > & {
          /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#variables:member} */
          variables?: TVariables;
        },
      ]
    : [
        options: useApolloMutation.MutationFunctionOptions<
          TData,
          TVariables,
          TCache
        > & {
          /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#variables:member} */
          variables: TVariables;
        },
      ]
) => Promise<Result<TData>>;

export const useMutation = <
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
  CV extends Partial<TVariables> = {},
>(
  mutation: TypedDocumentNode<TData, TVariables>,
  opts: Options<TData, TVariables, CV> = {}
) => {
  const {
    showErrorsWithSnackNotification,
    showErrorsInForm,
    sendErrors,
    warnIfNoErrorHandling = true,
    ...mutationOptions
  } = opts;

  const [mutate, { loading, called }] = useApolloMutation(
    mutation,
    mutationOptions as Parameters<
      typeof useApolloMutation<TData, TVariables>
    >[1]
  );
  const { setShowRestrictedAccess } = useRestrictedAccessContext() ?? {};
  const { setShowMangopayConsent } = useMangopayConsentContext() ?? {};

  const mutationName = useMemo(
    () =>
      (
        (
          mutation.definitions.find(
            d => d.kind === 'OperationDefinition' && d.operation === 'mutation'
          ) as OperationDefinitionNode
        ).selectionSet.selections[0] as FieldNode
      ).name.value,
    [mutation.definitions]
  );

  const handleErrors = useCallback(
    (errors: readonly Error[], networkError?: boolean) => {
      if (errors.length === 0) return;
      if (errors.some(({ code }) => code === errorCodes.consentRequired)) {
        setShowMangopayConsent?.(true);
        return;
      }
      if (errors.some(({ code }) => code === errorCodes.consentAlreadyGiven)) {
        setShowMangopayConsent?.(false);
        return;
      }
      if (errors.some(({ code }) => code === errorCodes.unverifiedEmail)) {
        setShowRestrictedAccess?.('email');
      } else if (
        errors.some(({ code }) => code === errorCodes.unverifiedPhoneNumber)
      ) {
        setShowRestrictedAccess?.('phone');
      }
      const message = errors.map(e => e.message).join(', ');
      if (showErrorsWithSnackNotification) {
        notificationsQueue.addNotification(
          'errors',
          { errors: message },
          { level: Level.ERROR }
        );
        return;
      }
      if (sendErrors) {
        sendSafeError(new Error(`Errors in graphql mutation: ${message}`));
        return;
      }
      if (showErrorsInForm) {
        return;
      }

      if (networkError) {
        throw new NetworkError(message);
      } else {
        throw new MutationError(message);
      }
    },
    [
      showErrorsWithSnackNotification,
      showErrorsInForm,
      sendErrors,
      setShowRestrictedAccess,
      setShowMangopayConsent,
    ]
  );

  return [
    useCallback(
      async (
        options?: useApolloMutation.MutationFunctionOptions<TData, TVariables>
      ) => {
        const errors: { message: string; code?: number }[] = [];
        let data: useApolloMutation.Result<TData>['data'];
        try {
          ({ data } = await mutate(
            ...([options] as Parameters<typeof mutate>)
          ));
          const mutationData = (
            data as unknown as Record<string, MutationPayload>
          )[mutationName];
          if (Array.isArray(mutationData?.errors)) {
            errors.push(...mutationData.errors);
            handleErrors(mutationData.errors);
          }
        } catch (e: unknown) {
          // v3 put GraphQL errors on `e.graphQLErrors` and transport failures on
          // `e.networkError`. v4 throws CombinedGraphQLErrors for the former and
          // the transport error itself for the latter.
          const graphQLErrors = CombinedGraphQLErrors.is(e)
            ? e.errors
            : undefined;
          // Re-thrown by handleErrors and already surfaced to the caller via
          // the returned `errors`, so not an unexpected error to report.
          const isExpectedBusinessError =
            e instanceof MutationError || e instanceof NetworkError;
          // v3 exposed anything that wasn't a GraphQL error as `networkError`.
          const networkError =
            !graphQLErrors && !isExpectedBusinessError && isErrorLike(e)
              ? e
              : undefined;
          // networkError is NOT an array
          if (networkError) {
            errors.push(networkError);
            handleErrors([networkError], true);
          }
          if (graphQLErrors) {
            errors.push(...graphQLErrors);
            handleErrors(graphQLErrors);
          }
          if (!graphQLErrors && !networkError && !isExpectedBusinessError) {
            if (
              warnIfNoErrorHandling &&
              import.meta.env.MODE !== 'production'
            ) {
              // this is to fail the tests intentionally
              // eslint-disable-next-line no-console
              console.warn(`${mutationName} doesn't request errors`, e);
            }
            // these are unexpected errors that aren't shown to the user
            // let's send them to sentry & fix one by one
            sendSafeError(e);
          }
        }
        return { data, errors, success: errors.length === 0 };
      },
      [handleErrors, mutate, mutationName, warnIfNoErrorHandling]
    ) as unknown as MutationFunction<
      TData,
      MakeRequiredVariablesOptional<TVariables, CV>
    >,
    { loading, called },
  ] as const;
};
